import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, CircleCheck, CornerDownLeft, RotateCcw, Share2 } from "lucide-react"
import { api, errorMessage } from "../api/client"
import { useApp } from "../state/AppState"
import { indexTags } from "../state/useProject"
import { subscribeIssues } from "../state/issueFeed"
import {
	Avatar,
	Button,
	Card,
	Input,
	Spinner,
	StatusBadge,
	cx
} from "../components/ui"
import { EntryBody, EntryByline, EntryCard } from "../components/issues/EntryCard"
import { AssignBar } from "../components/issues/AssignBar"
import { InviteDialog, MemberStrip } from "../components/issues/InviteDialog"
import { TagSelector } from "../components/issues/TagSelector"
import { ShareDialog } from "../components/issues/ShareDialog"
import { JumpToLatest } from "../components/issues/JumpToLatest"
import { RichTextEditor } from "../components/editor/RichTextEditor"
import { fullTimestampWithOffset, relativeTime } from "../lib/format"
import { NotFoundPage } from "./NotFoundPage"

export function IssueDetailPage() {
	const { projectId, issueId } = useParams()
	const { toast, user, timezone, refreshProjects, refreshPeople } = useApp()

	const [issue, setIssue] = useState(null)
	const [members, setMembers] = useState([])
	const [agents, setAgents] = useState([])
	const [cast, setCast] = useState([])
	const [project, setProject] = useState(null)
	const [tags, setTags] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [sharing, setSharing] = useState(false)
	const [inviting, setInviting] = useState(false)
	const [busy, setBusy] = useState(false)

	const [reply, setReply] = useState("")
	const [replyAttachments, setReplyAttachments] = useState([])
	const [editingTitle, setEditingTitle] = useState(false)
	const [titleDraft, setTitleDraft] = useState("")

	const load = useCallback(async () => {
		try {
			const [detail, issueResult] = await Promise.all([
				api.project(projectId),
				api.issue(projectId, issueId)
			])
			setProject(detail.project)
			setTags(detail.tags)
			setIssue(issueResult.issue)
			setMembers(issueResult.members || [])
			setAgents(issueResult.agents || [])
			setCast(issueResult.people || issueResult.members || [])
			setError(null)
		} catch (cause) {
			setError(cause)
		} finally {
			setLoading(false)
		}
	}, [projectId, issueId])

	useEffect(() => {
		setLoading(true)
		load()
	}, [load])

	// Built from `people`, not `members`. The member list is who was invited
	// to this issue; since a team can be put on a whole project, the person
	// who wrote an entry is not always on it, and indexing the invite list
	// leaves their byline reading "Unknown".
	const peopleById = useMemo(() => {
		const index = {}
		cast.forEach((person) => {
			index[person.user_id] = person
		})
		return index
	}, [cast])

	// The issue rail polls the project's issues, threads and all. Take this
	// issue from that same fetch rather than polling again, so the page and
	// the rail always agree. Only ever move forward: a fetch that started
	// before one of our own writes must not undo it.
	useEffect(
		() =>
			subscribeIssues((fromProject, issues) => {
				if (fromProject !== projectId) return
				const fresh = issues.find((candidate) => candidate.issue_id === issueId)
				if (!fresh) return
				setIssue((current) =>
					current && (fresh.updated_at || "") <= (current.updated_at || "")
						? current
						: fresh
				)
			}),
		[projectId, issueId]
	)

	// That fetch carries the issue but not the directory of who is in it. When
	// it names someone this page has not met - a new member, a new author - or
	// someone has left, load the issue in full, once per change.
	const directoryCheckedAt = useRef(null)
	useEffect(() => {
		if (!issue || directoryCheckedAt.current === issue.updated_at) return
		directoryCheckedAt.current = issue.updated_at
		const memberIds = issue.member_ids || []
		const named = [
			issue.created_by,
			...(issue.comments || []).flatMap((c) => [c.author_id, c.subject_id])
		]
		const stale =
			named.some((id) => id && !peopleById[id]) ||
			memberIds.length !== members.length ||
			memberIds.some((id) => !members.some((m) => m.user_id === id))
		if (stale) load()
	}, [issue, peopleById, members, load])

	async function postReply() {
		if (busy || (!reply.trim() && replyAttachments.length === 0)) return
		setBusy(true)
		try {
			const result = await api.addComment(projectId, issueId, {
				body: reply,
				attachment_ids: replyAttachments.map((a) => a.attachment_id)
			})
			setIssue(result.issue)
			setReply("")
			setReplyAttachments([])
			refreshProjects()
		} catch (cause) {
			toast(errorMessage(cause), "error")
		} finally {
			setBusy(false)
		}
	}

	async function toggleStatus() {
		setBusy(true)
		const next = issue.status === "open" ? "closed" : "open"
		try {
			const result = await api.setIssueStatus(projectId, issueId, next)
			setIssue(result.issue)
			refreshProjects()
			toast(
				next === "closed" ? "Issue resolved" : "Issue reopened",
				next === "closed" ? "success" : "info"
			)
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setBusy(false)
		}
	}

	async function assign(assigneeId, note) {
		setBusy(true)
		try {
			const result = await api.assignIssue(projectId, issueId, {
				assignee_id: assigneeId,
				note
			})
			setIssue(result.issue)
			refreshProjects()
			const assignee = [...members, ...agents].find((m) => m.user_id === assigneeId)
			// Assigning an agent puts it on the issue; reload to show it there.
			if (assignee?.is_agent && !members.includes(assignee)) load()
			toast(
				!assigneeId
					? "Assignment cleared"
					: assignee?.is_agent
						? `Handed to ${assignee.name}. It picks this up from its inbox.`
						: `${assignee?.name || "They"} have been emailed`,
				"success"
			)
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setBusy(false)
		}
	}

	async function patchIssue(payload) {
		const result = await api.updateIssue(projectId, issueId, payload)
		setIssue(result.issue)
		refreshProjects()
		return result.issue
	}

	async function removeMember(member) {
		try {
			const result = await api.removeMember(projectId, issueId, member.user_id)
			setIssue(result.issue)
			setMembers((current) => current.filter((m) => m.user_id !== member.user_id))
			toast(`${member.name} removed from this issue`, "info")
		} catch (cause) {
			toast(cause.message, "error")
		}
	}

	if (error?.status === 404) return <NotFoundPage />
	if (loading) return <Spinner label="Loading issue" />
	if (!issue) return null

	const author = peopleById[issue.created_by]
	const latestReply = [...(issue.comments || [])]
		.reverse()
		.find((comment) => (comment.kind || "comment") === "comment")
	const closed = issue.status === "closed"
	const tagsById = indexTags(tags)

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-3">
				<Link
					to={`/projects/${projectId}`}
					className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-ink-500 transition-colors hover:text-ink-900"
				>
					<ArrowLeft size={14} aria-hidden />
					{project?.name || "Back to issues"}
				</Link>
				<div className="flex items-center gap-2">
					<Button icon={Share2} size="sm" onClick={() => setSharing(true)}>
						Share
					</Button>
					<Button
						size="sm"
						variant={closed ? "secondary" : "primary"}
						icon={closed ? RotateCcw : CircleCheck}
						onClick={toggleStatus}
						busy={busy}
						disabled={busy}
					>
						{closed ? "Reopen" : "Mark resolved"}
					</Button>
				</div>
			</div>

			<Card className="overflow-hidden">
				{/* Filled header - the question is what this page is about. */}
				<div
					className={cx(
						"px-4 py-3.5",
						closed ? "bg-state-closed-soft" : "bg-state-open-soft"
					)}
				>
					<div className="flex flex-wrap items-start gap-x-3 gap-y-2">
						<span
							className={cx(
								"mt-1 text-sm font-semibold tabular",
								closed ? "text-state-closed-ink/60" : "text-state-open-ink/60"
							)}
						>
							#{issue.number}
						</span>
						<div className="min-w-0 flex-1">
							{editingTitle ? (
								<div className="flex gap-2">
									<Input
										value={titleDraft}
										onChange={(event) => setTitleDraft(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === "Enter") {
												patchIssue({ title: titleDraft.trim() })
													.then(() => setEditingTitle(false))
													.catch((cause) => toast(cause.message, "error"))
											}
											if (event.key === "Escape") setEditingTitle(false)
										}}
										autoFocus
										maxLength={300}
									/>
									<Button size="sm" onClick={() => setEditingTitle(false)}>
										Cancel
									</Button>
								</div>
							) : (
								<h1
									className={cx(
										"text-lg font-semibold leading-snug tracking-tight",
										closed ? "text-state-closed-ink" : "text-state-open-ink",
										issue.can_edit_issue && "cursor-text hover:underline"
									)}
									onClick={() => {
										if (!issue.can_edit_issue) return
										setTitleDraft(issue.title)
										setEditingTitle(true)
									}}
									title={issue.can_edit_issue ? "Click to rename" : undefined}
								>
									{issue.title}
								</h1>
							)}
						</div>
						<StatusBadge status={issue.status} />
					</div>
				</div>

				{/* The opening post sits with the title. A question and the detail
				    behind it are one thought, and two boxes made them read as two. */}
				<EntryByline
					author={author}
					entry={issue}
					timezone={timezone}
					className="px-4 pt-3.5"
				/>
				<EntryBody
					projectId={projectId}
					issueId={issueId}
					entry={issue}
					isRoot
					canEdit={issue.can_edit_issue}
					onSave={patchIssue}
					className="px-4 pb-4 pt-2"
				/>

				<div className="space-y-3 border-t border-ink-100 bg-ink-50/60 px-4 py-3">
					{closed && issue.closed_at && (
						<span
							className="flex items-center gap-1.5 text-2xs text-state-closed-ink"
							title={fullTimestampWithOffset(issue.closed_at, timezone)}
						>
							<CircleCheck size={12} aria-hidden />
							Resolved by {peopleById[issue.closed_by]?.name || "Unknown"}{" "}
							{relativeTime(issue.closed_at, timezone)}
						</span>
					)}

					<MemberStrip members={members} issue={issue} onRemove={removeMember} />

					<TagSelector
						projectId={projectId}
						tags={tags}
						selected={issue.tag_ids || []}
						onChange={(tagIds) =>
							patchIssue({ tag_ids: tagIds }).catch((cause) =>
								toast(cause.message, "error")
							)
						}
						onTagCreated={(tag) => setTags((current) => [...current, tag])}
						canCreate={Boolean(project?.is_owner)}
					/>
				</div>
			</Card>

			<AssignBar
				issue={issue}
				members={members}
				agents={agents}
				busy={busy}
				onAssign={assign}
				onInvite={() => setInviting(true)}
			/>

			{/* The opening post is in the card above, so the thread starts at the
			    first reply. */}
			<div className="space-y-2.5">
				{(issue.comments || []).map((comment) => (
					<EntryCard
						key={comment.comment_id}
						id={entryAnchor(comment.comment_id)}
						projectId={projectId}
						issueId={issueId}
						entry={comment}
						author={peopleById[comment.author_id]}
						subject={peopleById[comment.subject_id]}
						canEdit={comment.can_edit}
						onSave={async (payload) => {
							const result = await api.updateComment(
								projectId,
								issueId,
								comment.comment_id,
								payload
							)
							setIssue(result.issue)
						}}
					/>
				))}
			</div>

			<Card className="p-3.5">
				<div className="mb-2 flex items-center gap-2">
					<Avatar person={user} size="sm" />
					<span className="text-[0.8125rem] font-medium text-ink-900">{user?.name}</span>
					<span className="text-2xs text-ink-500">adds a reply</span>
				</div>
				<RichTextEditor
					projectId={projectId}
					issueId={issueId}
					value={reply}
					onChange={setReply}
					attachments={replyAttachments}
					onAttachmentsChange={setReplyAttachments}
					placeholder="Answer the question, or add what is still missing. Paste a screenshot straight in."
					onSubmit={postReply}
				/>
				<div className="mt-2.5 flex items-center justify-between gap-3">
					<p className="text-2xs leading-relaxed text-ink-500">
						Once someone replies after you, this entry is locked for good — nothing can
						be retracted.
					</p>
					<Button
						variant="primary"
						icon={CornerDownLeft}
						onClick={postReply}
						title="Ctrl+Enter"
						busy={busy}
						disabled={busy || (!reply.trim() && replyAttachments.length === 0)}
					>
						Post reply
					</Button>
				</div>
			</Card>

			<JumpToLatest
				targetId={latestReply ? entryAnchor(latestReply.comment_id) : null}
			/>

			<InviteDialog
				open={inviting}
				onClose={() => setInviting(false)}
				projectId={projectId}
				issue={issue}
				members={members}
				onInvited={async () => {
					await load()
					refreshPeople()
				}}
			/>

			<ShareDialog
				open={sharing}
				onClose={() => setSharing(false)}
				projectId={projectId}
				issueId={issueId}
				projectName={project?.name}
			/>
		</div>
	)
}

/** The DOM id of a reply, for jumping to it. */
function entryAnchor(commentId) {
	return `entry-${commentId}`
}
